import { type LoadingManager, Loader } from '@seddi/three';

export function isFetchProgressSupported() {
  return typeof Response !== 'undefined' && typeof ReadableStream !== 'undefined';
}

/**
 * Binary asset loader.
 */
export class BinaryAssetLoader extends Loader<Uint8Array> {
  readonly isBinaryAssetLoader: boolean;

  constructor(manager: LoadingManager) {
    super(manager);

    this.isBinaryAssetLoader = true;
    this.requestHeader = { 'Cache-Control': 'no-store' };
  }

  load(
    url: string,
    onLoad: (data: Uint8Array) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (err: unknown) => void,
  ) {
    if (url === undefined) url = '';

    if (this.path !== undefined) url = this.path + url;

    url = this.manager.resolveURL(url);

    const scope = this;
    const fetchOptions: any = {};
    fetchOptions.credentials = 'omit';
    fetchOptions.headers = this.requestHeader;

    // https://github.com/samundrak/fetch-progress
    fetch(url, fetchOptions)
      .then((res) => {
        if (!onProgress || !isFetchProgressSupported()) {
          return res;
        }
        const { body, headers, status } = res;
        const contentLength = headers.get('content-length');
        const progress = {
          lengthComputable: true,
          total: parseInt(contentLength, 10) || 0,
          loaded: 0,
        };
        const reader = body.getReader();
        const stream = new ReadableStream({
          start(controller) {
            function push() {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    controller.close();
                    return;
                  }
                  if (value) {
                    const chunk = value.length;
                    progress.loaded += chunk;
                  }
                  controller.enqueue(value);
                  push();
                  const ev = new ProgressEvent('progress', {
                    lengthComputable: progress.lengthComputable,
                    loaded: progress.loaded,
                    total: progress.total,
                  });
                  onProgress(ev);
                })
                .catch((err) => {
                  onError(err);
                });
            }
            push();
          },
        });
        return new Response(stream, { headers, status });
      })
      .then((res: Response) => {
        return res.arrayBuffer();
      })
      .then((buffer: ArrayBuffer) => {
        const bytes = new Uint8Array(buffer);
        if (onLoad) onLoad(bytes);

        scope.manager.itemEnd(url);
      })
      .catch(function (err) {
        if (onError) onError(err);

        scope.manager.itemError(url);
        scope.manager.itemEnd(url);
      });

    scope.manager.itemStart(url);
  }
}

/**
 * Load binary assset.
 * @param input - URL.
 * @param manager - Loading manager.
 * @param onProgress - Progress handler.
 */
export const load = (
  input: string,
  manager: LoadingManager,
  onProgress?: (event: ProgressEvent) => void,
): Promise<Uint8Array> => {
  const loader = new BinaryAssetLoader(manager);
  return new Promise((resolve, reject) => {
    loader.load(input, resolve, onProgress, reject);
  });
};
